import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";

import type { ActiveSurvey } from "../types";

// AIDEV-NOTE: Hook to fetch active surveys for the current user and route
// Server-side filters by: user type, route patterns, completion/dismissal status
// Client-side filtering prevents recently completed surveys from reappearing due to race conditions

// AIDEV-NOTE: Track recently completed surveys to prevent double-showing
// Maps surveyId -> completion timestamp
const recentlyCompleted = new Map<string, number>();
const COMPLETION_GRACE_PERIOD = 30000; // 30 seconds

export function markSurveyCompleted(surveyId: string) {
    recentlyCompleted.set(surveyId, Date.now());

    // Clean up old entries after grace period
    setTimeout(() => {
        const now = Date.now();
        for (const [id, timestamp] of recentlyCompleted.entries()) {
            if (now - timestamp > COMPLETION_GRACE_PERIOD) {
                recentlyCompleted.delete(id);
            }
        }
    }, COMPLETION_GRACE_PERIOD);
}

export function useActiveSurveys(currentPath: string, enabled = true) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: ["surveys", "active", currentPath],
        enabled,
        queryFn: async () => {
            const response = await apiClient.get("/surveys/active", {
                params: { path: currentPath },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch active surveys");
            }

            const data = (await response.json()) as { surveys: ActiveSurvey[] };

            // AIDEV-NOTE: Filter out recently completed surveys on client-side
            // This prevents race conditions where server hasn't fully processed deletion yet
            const now = Date.now();
            const filtered = data.surveys.filter((survey) => {
                const completedAt = recentlyCompleted.get(survey.id);
                if (
                    completedAt &&
                    now - completedAt < COMPLETION_GRACE_PERIOD
                ) {
                    return false; // Filter out recently completed
                }
                return true;
            });

            return filtered;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
        // AIDEV-NOTE: No polling needed - event-triggered surveys now use proper invalidation
        // trackEvent() calls queue-survey endpoint which dispatches "survey-trigger-checked" event
        // SurveyProvider listens for this event and invalidates the query
    });
}
